/**
 * Orquestador de tours: escucha tour:start y tour:start-forced.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import FeatureTour from './FeatureTour'
import { ALL_TOURS, TOUR_DOM_READY_DELAY_MS, shouldShowTour } from '../config/tourSteps'
import { ALL_NEWS_TOURS, NEWS_FIRMA_GUIADA_ID, prepararNovedadFirmaGuiada, usuarioEnSesionId } from '../config/newsTours'
import { puedeAbrirRuta, usuarioDeSesion } from '../utils/accesoRutas'
import { decidirNavegacionTour } from '../utils/tourNavegacion'

function isNewsTourId(tourId) {
  return tourId && ALL_NEWS_TOURS[tourId]
}

function getTourDefinition(tourId) {
  return ALL_TOURS[tourId] || ALL_NEWS_TOURS[tourId]
}

const NAV_DELAY_MS = 800

/** Tras el tour `signature-config-page` en el paso `tour-signature-add`, se encadena `signature-create-modal` y luego se reanuda en este índice (nota + ayuda). */
const SIGNATURE_CONFIG_RESUME_STEP_INDEX = 5

const pendingQueue = []
let managerReady = false
let managerCallback = null

function enqueue(tourId, forced = false) {
  if (managerReady && managerCallback) {
    managerCallback(tourId, forced)
  } else {
    pendingQueue.push({ tourId, forced })
  }
}

;(function installEarlyListeners() {
  const handle = (e, forced = false) => {
    const id = e.detail?.tourId
    if (id) enqueue(id, forced)
  }
  window.addEventListener('tour:start', (e) => handle(e, false))
  window.addEventListener('tour:start-forced', (e) => handle(e, true))
})()

const TourManager = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [activeTourId, setActiveTourId] = useState(null)
  const [stepIndex, setStepIndex] = useState(0)
  const [navigating, setNavigating] = useState(false)
  const [informativo, setInformativo] = useState(false)

  const tourIdRef = useRef(null)
  const stepIndexRef = useRef(0)
  const startDelayRef = useRef(null)
  const bloqueoRef = useRef(new Map())
  const intentoRef = useRef(null)
  /** Si hay valor, al cerrar `signature-create-modal` se reanuda `signature-config-page` en este índice. */
  const resumeSignatureConfigRef = useRef(null)
  tourIdRef.current = activeTourId
  stepIndexRef.current = stepIndex

  const currentTour = activeTourId ? getTourDefinition(activeTourId) : null
  const stepsLen = currentTour?.steps?.length ?? 0
  const safeStepIndex = stepsLen > 0 ? Math.min(Math.max(0, stepIndex), stepsLen - 1) : 0
  const currentStep = currentTour?.steps?.[safeStepIndex] ?? null

  const startTour = useCallback((tourId, forced = false) => {
    const tour = getTourDefinition(tourId)
    if (!tour) {
      console.warn('[TourManager] Tour desconocido:', tourId)
      return
    }
    if (window.__releaseAnnouncementActive) return
    if (isNewsTourId(tourIdRef.current) && !isNewsTourId(tourId)) return
    if (tourId === NEWS_FIRMA_GUIADA_ID) prepararNovedadFirmaGuiada(usuarioEnSesionId())
    if (!forced && !shouldShowTour(tour)) return
    if (startDelayRef.current) {
      clearTimeout(startDelayRef.current)
      startDelayRef.current = null
    }
    startDelayRef.current = setTimeout(() => {
      startDelayRef.current = null
      bloqueoRef.current = new Map()
      intentoRef.current = null
      setActiveTourId(tourId)
      setStepIndex(0)
      setNavigating(false)
      setInformativo(false)
    }, TOUR_DOM_READY_DELAY_MS)
  }, [])

  const closeTour = useCallback(() => {
    const tid = tourIdRef.current
    const tour = getTourDefinition(tid)
    if (tid === 'signature-create-modal' && resumeSignatureConfigRef.current != null) {
      const resumeIdx = resumeSignatureConfigRef.current
      resumeSignatureConfigRef.current = null
      if (tour?.storageKey) {
        try {
          localStorage.setItem(tour.storageKey, String(tour.version ?? 'v1'))
        } catch {
          /* ignore */
        }
      }
      if (startDelayRef.current) {
        clearTimeout(startDelayRef.current)
        startDelayRef.current = null
      }
      window.dispatchEvent(new CustomEvent('tour:signature-close-create-form'))
      setActiveTourId('signature-config-page')
      setStepIndex(resumeIdx)
      setNavigating(false)
      return
    }
    resumeSignatureConfigRef.current = null
    if (tour) {
      localStorage.setItem(tour.storageKey, String(tour.version ?? 'v1'))
    }
    if (isNewsTourId(tid)) {
      window.dispatchEvent(new CustomEvent('app-news:closed', { detail: { tourId: tid } }))
    }
    if (startDelayRef.current) {
      clearTimeout(startDelayRef.current)
      startDelayRef.current = null
    }
    bloqueoRef.current = new Map()
    intentoRef.current = null
    setActiveTourId(null)
    setStepIndex(0)
    setNavigating(false)
    setInformativo(false)
  }, [])

  const handleNext = useCallback(() => {
    const tid = tourIdRef.current
    const idx = stepIndexRef.current
    const tour = getTourDefinition(tid)
    const step = tour?.steps?.[idx]
    if (tid === 'signature-config-page' && step?.targetId === 'tour-signature-add') {
      resumeSignatureConfigRef.current = SIGNATURE_CONFIG_RESUME_STEP_INDEX
      window.dispatchEvent(new CustomEvent('tour:signature-open-create-form'))
      if (startDelayRef.current) {
        clearTimeout(startDelayRef.current)
        startDelayRef.current = null
      }
      startDelayRef.current = setTimeout(() => {
        startDelayRef.current = null
        setActiveTourId('signature-create-modal')
        setStepIndex(0)
        setNavigating(false)
      }, TOUR_DOM_READY_DELAY_MS)
      return
    }
    setStepIndex((prev) => prev + 1)
  }, [])

  const handlePrev = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1))
  }, [])

  useEffect(() => {
    window.__tourActive = !!activeTourId
    window.__activeTourId = activeTourId
    window.__tourStepIndex = stepIndex
    window.dispatchEvent(
      new CustomEvent('tour:step', { detail: { tourId: activeTourId, stepIndex } })
    )
  }, [activeTourId, stepIndex])

  useEffect(() => {
    managerReady = true
    managerCallback = startTour
    while (pendingQueue.length > 0) {
      const { tourId, forced } = pendingQueue.shift()
      startTour(tourId, forced)
      break
    }
    return () => {
      managerReady = false
      managerCallback = null
      window.__tourActive = false
    }
  }, [startTour])

  useEffect(() => {
    return () => {
      if (startDelayRef.current) {
        clearTimeout(startDelayRef.current)
        startDelayRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!activeTourId || !currentStep) return undefined
    const key = `${activeTourId}:${safeStepIndex}`
    const pagina = currentStep.page || ''
    const targetId = currentStep.targetId || ''
    const mostrar = (esInformativo, motivo = 'denegado') => {
      if (esInformativo && !bloqueoRef.current.get(key)) bloqueoRef.current.set(key, motivo)
      setNavigating(false)
      setInformativo(esInformativo)
    }

    const bloqueo = bloqueoRef.current.get(key)
    const permitido = !pagina || puedeAbrirRuta(pagina, usuarioDeSesion())
    if (bloqueo === 'denegado' && pagina && location.pathname === pagina && permitido) {
      bloqueoRef.current.delete(key)
    } else if (bloqueo) {
      mostrar(true)
      return undefined
    }

    const decision = decidirNavegacionTour({
      pagina,
      pathname: location.pathname,
      permitido,
      yaIntento: intentoRef.current === key,
      targetExiste: undefined,
    })

    if (decision.navegar) {
      intentoRef.current = key
      setInformativo(false)
      setNavigating(true)
      navigate(pagina)
      const timer = window.setTimeout(() => {
        if (window.location.pathname !== pagina) mostrar(true)
      }, NAV_DELAY_MS)
      return () => window.clearTimeout(timer)
    }

    if (decision.informativo) {
      mostrar(true)
      return undefined
    }

    if (pagina && location.pathname === pagina && targetId && !document.getElementById(targetId)) {
      setInformativo(false)
      setNavigating(true)
      const timer = window.setTimeout(() => {
        if (document.getElementById(targetId)) {
          mostrar(false)
          return
        }
        mostrar(true, 'sin-target')
      }, NAV_DELAY_MS)
      return () => window.clearTimeout(timer)
    }

    mostrar(false)
    return undefined
  }, [activeTourId, safeStepIndex, currentStep, location.pathname, navigate])

  if (!currentTour) return null

  return (
    <FeatureTour
      isOpen={!!activeTourId}
      steps={currentTour.steps}
      stepIndex={stepIndex}
      onNext={handleNext}
      onPrev={handlePrev}
      onClose={closeTour}
      label={currentTour.label}
      navigating={navigating}
      informativo={informativo}
    />
  )
}

export default TourManager
